import { createListItem } from "./listItem";
import type { BriefBlock, BriefBlockType } from "./schema";

export type BlockDefinition<T extends BriefBlock = BriefBlock> = {
    type: T["type"];
    label: string;
    create: () => T;
};

export const blockDefinitions: {
    [K in BriefBlockType]: BlockDefinition<Extract<BriefBlock, { type: K }>>;
} = {
    title: {
        type: "title",
        label: "Titre",
        create: () => ({
            id: crypto.randomUUID(),
            type: "title",
            text: "",
            anchor: null,
            level: 1,
        }),
    },
    body: {
        type: "body",
        label: "Paragraphe",
        create: () => ({
            id: crypto.randomUUID(),
            type: "body",
            html: "",
        }),
    },
    listItem: {
        type: "listItem",
        label: "Puce",
        create: () => createListItem(),
    },
};

export function createBlockFrom(block: BriefBlock, content = ""): BriefBlock {
    switch (block.type) {
        case "title":
            return { ...blockDefinitions.title.create(), text: content, level: block.level };
        case "body":
            return { ...blockDefinitions.body.create(), html: content };
        case "listItem":
            return createListItem({
                ordered: block.ordered,
                depth: block.depth,
                html: content,
            });
    }
}

export function setBlockContent(block: BriefBlock, content: string): BriefBlock {
    switch (block.type) {
        case "title":
            return { ...block, text: content };
        case "body":
        case "listItem":
            return { ...block, html: content };
    }
}

export function getBlockTextLength(block: BriefBlock): number {
    switch (block.type) {
        case "title":
            return block.text.length;
        case "body":
        case "listItem":
            return htmlToPlainText(block.html).length;
    }
}

export function mergeBlocks(target: BriefBlock, source: BriefBlock): BriefBlock {
    switch (target.type) {
        case "title": {
            const extra = source.type === "title" ? source.text : htmlToPlainText(source.html);
            return { ...target, text: target.text + extra };
        }
        case "body":
        case "listItem": {
            const extra = source.type === "title" ? plainTextToHtml(source.text) : source.html;
            return { ...target, html: target.html + extra };
        }
    }
}

function htmlToPlainText(html: string): string {
    const template = document.createElement("template");
    template.innerHTML = html;
    return template.content.textContent ?? "";
}

function plainTextToHtml(text: string): string {
    return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
